import React, { useState, useEffect, useCallback } from 'react';
import serverService from '../../services/serverService';

const ServerMonitor = ({ activeSection: initialActiveSection = 'system' }) => {
  const [activeSection, setActiveSection] = useState(initialActiveSection);
  const [systemInfo, setSystemInfo] = useState(null);
  const [pm2Processes, setPm2Processes] = useState([]);
  const [nginxStatus, setNginxStatus] = useState(null);
  const [dbInfo, setDbInfo] = useState(null);
  const [logs, setLogs] = useState('');
  const [logType, setLogType] = useState('pm2-error');
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [processLogs, setProcessLogs] = useState('');
  const [loading, setLoading] = useState({
    system: false,
    pm2: false,
    nginx: false,
    db: false,
    logs: false
  });
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);

  // 시스템 정보 로드
  const loadSystemInfo = useCallback(async () => {
    setLoading(prev => ({ ...prev, system: true }));
    setError(null);
    try {
      const result = await serverService.getSystemInfo();
      if (result.success) {
        setSystemInfo(result.data);
      } else {
        setError(result.error || '시스템 정보를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('System info error:', err);
      setError(`시스템 정보를 불러오는데 실패했습니다: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, system: false }));
    }
  }, []);

  // PM2 프로세스 로드
  const loadPM2Processes = useCallback(async () => {
    setLoading(prev => ({ ...prev, pm2: true }));
    try {
      const result = await serverService.getPM2Processes();
      if (result.success) {
        setPm2Processes(result.data);
      } else {
        console.error('PM2 processes error:', result.error);
      }
    } catch (err) {
      console.error('PM2 processes error:', err);
      setError(`PM2 프로세스 정보를 불러오는데 실패했습니다: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, pm2: false }));
    }
  }, []);

  // Nginx 상태 로드
  const loadNginxStatus = useCallback(async () => {
    setLoading(prev => ({ ...prev, nginx: true }));
    try {
      const result = await serverService.getNginxStatus();
      if (result.success) {
        setNginxStatus(result.data);
      } else {
        console.error('Nginx status error:', result.error);
      }
    } catch (err) {
      console.error('Nginx status error:', err);
      setError(`Nginx 상태를 불러오는데 실패했습니다: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, nginx: false }));
    }
  }, []);

  // DB 정보 로드
  const loadDbInfo = useCallback(async () => {
    setLoading(prev => ({ ...prev, db: true }));
    try {
      const result = await serverService.getDatabaseInfo();
      if (result.success) {
        setDbInfo(result.data);
      } else {
        console.error('Database info error:', result.error);
      }
    } catch (err) {
      console.error('Database info error:', err);
      setError(`데이터베이스 정보를 불러오는데 실패했습니다: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, db: false }));
    }
  }, []);

  // 로그 로드
  const loadLogs = useCallback(async (type) => {
    setLoading(prev => ({ ...prev, logs: true }));
    try {
      const result = await serverService.getLogs(type);
      if (result.success) {
        setLogs(result.data.logs);
      }
    } catch (err) {
      setLogs('로그를 불러오는데 실패했습니다.');
    } finally {
      setLoading(prev => ({ ...prev, logs: false }));
    }
  }, []);

  // PM2 프로세스 재시작
  const handleRestartProcess = async (name) => {
    if (!window.confirm(`${name} 프로세스를 재시작하시겠습니까?`)) return;

    try {
      const result = await serverService.restartPM2Process(name);
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        loadPM2Processes();
      } else {
        setMessage({ type: 'error', text: result.error || '재시작 실패' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '프로세스 재시작 중 오류가 발생했습니다.' });
    }
  };

  // PM2 프로세스 로그 보기
  const handleViewProcessLogs = async (name) => {
    setSelectedProcess(name);
    try {
      const result = await serverService.getPM2Logs(name);
      if (result.success) {
        setProcessLogs(result.data.logs);
      }
    } catch (err) {
      setProcessLogs('로그를 불러오는데 실패했습니다.');
    }
  };

  // activeSection prop 변경 시 동기화
  useEffect(() => {
    setActiveSection(initialActiveSection);
  }, [initialActiveSection]);

  // 초기 로드
  useEffect(() => {
    loadSystemInfo();
    loadPM2Processes();
    loadNginxStatus();
    loadDbInfo();
  }, [loadSystemInfo, loadPM2Processes, loadNginxStatus, loadDbInfo]);

  // 자동 새로고침
  useEffect(() => {
    if (refreshInterval) {
      const interval = setInterval(() => {
        loadSystemInfo();
        loadPM2Processes();
      }, refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, loadSystemInfo, loadPM2Processes]);

  // 메시지 자동 숨김
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // 바이트 포맷팅
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 프로세스 업타임 포맷팅
  const formatProcessUptime = (uptime) => {
    if (!uptime) return '-';
    const now = Date.now();
    const diff = now - uptime;
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);

    if (days > 0) return `${days}일 ${hours}시간`;
    if (hours > 0) return `${hours}시간 ${minutes}분`;
    return `${minutes}분`;
  };

  // Gauge fill 색상 결정
  const getGaugeFillClass = (value) => {
    const numValue = parseFloat(value);
    if (numValue > 80) return 'bg-red-500';
    if (numValue > 50) return 'bg-amber-500';
    return 'bg-blue-500';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8 shadow-sm">
      {/* 헤더 */}
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 tracking-tight mb-1">서버 모니터링</h3>
          <p className="text-xs text-gray-500 leading-relaxed">서버 상태 및 프로세스를 관리합니다.</p>
        </div>
        <div className="flex gap-2">
          <select
            value={refreshInterval || ''}
            onChange={(e) => setRefreshInterval(e.target.value ? parseInt(e.target.value) : null)}
            className="px-4 py-2 border border-gray-200 rounded-md text-xs text-gray-900 bg-white cursor-pointer font-medium transition-all duration-150 hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
          >
            <option value="">수동 새로고침</option>
            <option value="5">5초마다</option>
            <option value="10">10초마다</option>
            <option value="30">30초마다</option>
            <option value="60">1분마다</option>
          </select>
          <button
            className="px-4 py-2 bg-blue-500 text-white border-none rounded-md text-xs font-medium cursor-pointer transition-all duration-150 min-h-[44px] hover:bg-blue-600 hover:shadow-md active:translate-y-px"
            onClick={() => {
              loadSystemInfo();
              loadPM2Processes();
              loadNginxStatus();
              loadDbInfo();
            }}
          >
            새로고침
          </button>
        </div>
      </div>

      {/* 메시지 */}
      {message && (
        <div className={`p-4 rounded-md mb-4 text-base leading-relaxed font-medium ${
          message.type === 'success'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 text-red-800 border border-red-200 rounded-md mb-4 text-base leading-relaxed font-medium">
          {error}
        </div>
      )}

      {/* 섹션 탭 */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 pb-0 overflow-x-auto">
        {[
          { key: 'system', label: '시스템' },
          { key: 'pm2', label: 'PM2 프로세스' },
          { key: 'services', label: '서비스' },
          { key: 'database', label: '데이터베이스' },
          { key: 'logs', label: '로그' }
        ].map(tab => (
          <button
            key={tab.key}
            className={`px-6 py-4 bg-transparent border-none text-base font-medium cursor-pointer border-b-2 -mb-px transition-all duration-150 whitespace-nowrap min-h-[44px] ${
              activeSection === tab.key
                ? 'text-blue-500 border-blue-500 font-bold'
                : 'text-gray-500 border-transparent hover:text-gray-900 hover:bg-gray-50'
            }`}
            onClick={() => {
              setActiveSection(tab.key);
              if (tab.key === 'logs') loadLogs(logType);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 시스템 모니터링 */}
      {activeSection === 'system' && (
        <div className="min-h-[200px]">
          {loading.system ? (
            <div className="flex items-center justify-center min-h-[200px] text-gray-500 text-sm">로딩 중...</div>
          ) : systemInfo ? (
            <>
              {/* 리소스 게이지 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {/* CPU */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 transition-all duration-150 hover:shadow-md hover:-translate-y-px">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-base font-bold text-gray-900">CPU</span>
                    <span className="text-2xl font-bold text-blue-500 tracking-tight">{systemInfo.cpu.usage}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-md overflow-hidden mb-4">
                    <div
                      className={`h-full rounded-md transition-all duration-400 ${getGaugeFillClass(systemInfo.cpu.usage)}`}
                      style={{ width: `${systemInfo.cpu.usage}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 font-medium">
                    <span>{systemInfo.cpu.cores} 코어</span>
                    <span className="max-w-[60%] truncate">{systemInfo.cpu.model}</span>
                  </div>
                </div>

                {/* 메모리 */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 transition-all duration-150 hover:shadow-md hover:-translate-y-px">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-base font-bold text-gray-900">메모리</span>
                    <span className="text-2xl font-bold text-blue-500 tracking-tight">{systemInfo.memory.percentage}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-md overflow-hidden mb-4">
                    <div
                      className={`h-full rounded-md transition-all duration-400 ${getGaugeFillClass(systemInfo.memory.percentage)}`}
                      style={{ width: `${systemInfo.memory.percentage}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 font-medium">
                    <span>{formatBytes(systemInfo.memory.used)} / {formatBytes(systemInfo.memory.total)}</span>
                  </div>
                </div>

                {/* 디스크 */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 transition-all duration-150 hover:shadow-md hover:-translate-y-px">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-base font-bold text-gray-900">디스크</span>
                    <span className="text-2xl font-bold text-blue-500 tracking-tight">{systemInfo.disk.percentage}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-md overflow-hidden mb-4">
                    <div
                      className={`h-full rounded-md transition-all duration-400 ${getGaugeFillClass(systemInfo.disk.percentage)}`}
                      style={{ width: `${systemInfo.disk.percentage}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 font-medium">
                    <span>{formatBytes(systemInfo.disk.used)} / {formatBytes(systemInfo.disk.total)}</span>
                  </div>
                </div>
              </div>

              {/* 시스템 정보 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: '업타임', value: systemInfo.uptime.formatted },
                  { label: 'Node.js', value: systemInfo.node.version },
                  { label: '호스트명', value: systemInfo.platform.hostname },
                  { label: 'OS', value: `${systemInfo.platform.type} ${systemInfo.platform.arch}` }
                ].map(item => (
                  <div key={item.label} className="bg-gray-50 rounded-md p-6 text-center border border-gray-200 transition-all duration-150 hover:shadow-md">
                    <div className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wider">{item.label}</div>
                    <div className="text-base font-bold text-gray-900">{item.value}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center min-h-[200px] text-gray-500 text-sm">시스템 정보를 불러올 수 없습니다.</div>
          )}
        </div>
      )}

      {/* PM2 프로세스 관리 */}
      {activeSection === 'pm2' && (
        <div className="min-h-[200px]">
          {loading.pm2 ? (
            <div className="flex items-center justify-center min-h-[200px] text-gray-500 text-sm">로딩 중...</div>
          ) : pm2Processes.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pm2Processes.map((proc) => (
                  <div
                    key={proc.pm_id}
                    className={`bg-gray-50 rounded-xl p-6 border border-gray-200 transition-all duration-150 hover:shadow-md ${
                      proc.status === 'online'
                        ? 'border-l-4 border-l-green-500'
                        : 'border-l-4 border-l-red-500'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-base font-bold text-gray-900 tracking-tight">{proc.name}</span>
                      <span className={`px-4 py-1 rounded-xl text-xs font-medium ${
                        proc.status === 'online'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {proc.status === 'online' ? '실행 중' : proc.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      {[
                        { label: 'PID', value: proc.pid || '-' },
                        { label: 'CPU', value: `${proc.cpu}%` },
                        { label: '메모리', value: formatBytes(proc.memory) },
                        { label: '업타임', value: formatProcessUptime(proc.uptime) },
                        { label: '재시작', value: `${proc.restarts}회` }
                      ].map(metric => (
                        <div key={metric.label} className="text-center p-2 bg-white rounded-md">
                          <span className="block text-xs text-gray-500 mb-1 uppercase tracking-wider font-medium">{metric.label}</span>
                          <span className="text-base font-bold text-gray-900">{metric.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="flex-1 px-4 py-2 border border-amber-300 bg-amber-100 text-amber-800 rounded-md text-xs font-medium cursor-pointer transition-all duration-150 min-h-[44px] hover:bg-amber-200 hover:-translate-y-px hover:shadow-md active:translate-y-0"
                        onClick={() => handleRestartProcess(proc.name)}
                      >
                        재시작
                      </button>
                      <button
                        className="flex-1 px-4 py-2 border border-gray-300 bg-gray-200 text-gray-700 rounded-md text-xs font-medium cursor-pointer transition-all duration-150 min-h-[44px] hover:bg-gray-300 hover:-translate-y-px hover:shadow-md active:translate-y-0"
                        onClick={() => handleViewProcessLogs(proc.name)}
                      >
                        로그
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* 프로세스 로그 모달 */}
              {selectedProcess && (
                <div
                  className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000] p-6 backdrop-blur-sm"
                  onClick={() => setSelectedProcess(null)}
                >
                  <div
                    className="bg-white rounded-xl w-full max-w-3xl max-h-[80vh] flex flex-col shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex justify-between items-center p-6 border-b border-gray-200">
                      <h4 className="m-0 text-base font-bold text-gray-900">{selectedProcess} 로그</h4>
                      <button
                        className="w-11 h-11 border-none bg-transparent text-2xl cursor-pointer text-gray-500 rounded-md transition-all duration-150 hover:bg-gray-50 hover:text-gray-900"
                        onClick={() => setSelectedProcess(null)}
                      >
                        ×
                      </button>
                    </div>
                    <pre className="flex-1 overflow-auto p-6 m-0 text-xs font-mono bg-gray-900 text-green-500 whitespace-pre-wrap break-all leading-relaxed">
                      {processLogs || '로그가 없습니다.'}
                    </pre>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center min-h-[200px] text-gray-500 text-sm">PM2 프로세스가 없습니다.</div>
          )}
        </div>
      )}

      {/* 서비스 상태 (Nginx) */}
      {activeSection === 'services' && (
        <div className="min-h-[200px]">
          {loading.nginx ? (
            <div className="flex items-center justify-center min-h-[200px] text-gray-500 text-sm">로딩 중...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Nginx */}
              <div className={`flex items-center gap-4 bg-gray-50 rounded-xl p-5 border-l-4 ${
                nginxStatus?.isRunning ? 'border-l-green-500' : 'border-l-red-500'
              }`}>
                <div className="text-3xl">🌐</div>
                <div className="flex-1">
                  <div className="text-base font-semibold text-gray-900">Nginx</div>
                  <div className="text-xs text-gray-500">v{nginxStatus?.version || '-'}</div>
                </div>
                <div className={`px-3 py-1.5 rounded-2xl text-xs font-medium ${
                  nginxStatus?.isRunning ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {nginxStatus?.isRunning ? '실행 중' : '중지됨'}
                </div>
              </div>

              {/* Node.js Backend */}
              <div className={`flex items-center gap-4 bg-gray-50 rounded-xl p-5 border-l-4 ${
                pm2Processes.some(p => p.name === 'ilouli-api' && p.status === 'online')
                  ? 'border-l-green-500'
                  : 'border-l-red-500'
              }`}>
                <div className="text-3xl">⚙️</div>
                <div className="flex-1">
                  <div className="text-base font-semibold text-gray-900">Backend API</div>
                  <div className="text-xs text-gray-500">{systemInfo?.node?.version || '-'}</div>
                </div>
                <div className={`px-3 py-1.5 rounded-2xl text-xs font-medium ${
                  pm2Processes.some(p => p.name === 'ilouli-api' && p.status === 'online')
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {pm2Processes.some(p => p.name === 'ilouli-api' && p.status === 'online') ? '실행 중' : '중지됨'}
                </div>
              </div>

              {/* SQLite */}
              <div className="flex items-center gap-4 bg-gray-50 rounded-xl p-5 border-l-4 border-l-green-500">
                <div className="text-3xl">🗄️</div>
                <div className="flex-1">
                  <div className="text-base font-semibold text-gray-900">SQLite</div>
                  <div className="text-xs text-gray-500">{dbInfo?.fileSizeFormatted || '-'}</div>
                </div>
                <div className="px-3 py-1.5 rounded-2xl text-xs font-medium bg-green-100 text-green-800">정상</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 데이터베이스 */}
      {activeSection === 'database' && (
        <div className="min-h-[200px]">
          {loading.db ? (
            <div className="flex items-center justify-center min-h-[200px] text-gray-500 text-sm">로딩 중...</div>
          ) : dbInfo ? (
            <>
              {/* DB 요약 */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {[
                  { value: dbInfo.fileSizeFormatted, label: 'DB 크기' },
                  { value: dbInfo.recentSignups?.today || 0, label: '오늘 가입' },
                  { value: dbInfo.recentSignups?.thisWeek || 0, label: '이번 주 가입' }
                ].map(stat => (
                  <div key={stat.label} className="bg-gray-50 rounded-xl p-5 text-center">
                    <span className="block text-3xl font-bold text-blue-500 mb-1">{stat.value}</span>
                    <span className="text-sm text-gray-500">{stat.label}</span>
                  </div>
                ))}
              </div>

              {/* 테이블별 레코드 수 */}
              <div>
                <h4 className="text-base font-semibold mb-4 text-gray-900">테이블별 레코드 수</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {Object.entries(dbInfo.tables).map(([table, count]) => (
                    <div key={table} className="flex justify-between items-center px-4 py-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-900 font-mono">{table}</span>
                      <span className="text-sm font-semibold text-blue-500">{count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center min-h-[200px] text-gray-500 text-sm">데이터베이스 정보를 불러올 수 없습니다.</div>
          )}
        </div>
      )}

      {/* 로그 뷰어 */}
      {activeSection === 'logs' && (
        <div className="min-h-[200px]">
          <div className="flex gap-3 mb-4">
            <select
              value={logType}
              onChange={(e) => {
                setLogType(e.target.value);
                loadLogs(e.target.value);
              }}
              className="flex-1 max-w-xs px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white"
            >
              <option value="pm2-error">PM2 에러 로그</option>
              <option value="nginx-access">Nginx 접속 로그</option>
              <option value="nginx-error">Nginx 에러 로그</option>
            </select>
            <button
              className="px-5 py-2.5 bg-blue-500 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-blue-600 disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={() => loadLogs(logType)}
              disabled={loading.logs}
            >
              {loading.logs ? '로딩 중...' : '새로고침'}
            </button>
          </div>
          <pre className="bg-gray-900 text-green-500 p-5 rounded-xl text-xs font-mono leading-relaxed whitespace-pre-wrap break-all max-h-[500px] overflow-auto m-0">
            {logs || '로그가 없습니다.'}
          </pre>
        </div>
      )}
    </div>
  );
};

export default ServerMonitor;
